// Réglages de l'application terrain : compte et session locale (code), synchronisation forcée,
// carte hors connexion, installation (PWA) et service worker, données locales (vider le cache),
// simulateur de l'API hors production, déconnexion.
import { Bouton, Champ, ConfirmationIntegree, Formulaire, type ErreurChamp } from '@cc/ui';
import { Download, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSession } from '../../session/FournisseurSession';
import { activerSimulateur, basculeAutomatiquePermise, simulateurActif } from '../../terrain/api';
import { dateHeure } from '../../terrain/etat-local';
import { tailleLisible } from '../../terrain/photos';
import { proposerInstallation } from '../../terrain/pwa';
import { codeValide, definirCodeLocal, fermerSessionLocale } from '../../terrain/session-locale';
import { simulateurPartage } from '../../terrain/simulateur';
import { supprimerBase, tailleDonnees, viderDonneesTravail } from '../../terrain/stockage';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { useSynchronisation, useTravailLocal } from '../../terrain/useTerrain';
import { Definitions, EnTeteTerrain, Section, TexteEtat } from './commun';
import { useZoneTerrain } from './CoquilleTerrain';
import { BlocSynchronisation } from './PageMonTravail';
import { SectionCarteHorsConnexion } from './SectionCarteHorsConnexion';
import styles from './terrain.module.css';

function SectionCode() {
  const { rafraichirSession } = useZoneTerrain();
  const [ouvert, setOuvert] = useState(false);
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreurs, setErreurs] = useState<ErreurChamp[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const changer = async () => {
    const fautes: ErreurChamp[] = [];
    if (!codeValide(code)) fautes.push({ champ: 'nouveau-code', message: 'Le code comporte six chiffres' });
    if (confirmation !== code) fautes.push({ champ: 'nouveau-code-confirmation', message: 'Les deux codes ne sont pas identiques' });
    setErreurs(fautes);
    if (fautes.length) return;
    await definirCodeLocal(code);
    await rafraichirSession();
    setOuvert(false);
    setCode('');
    setConfirmation('');
    setMessage('Code local modifié sur cet appareil.');
  };
  if (!ouvert) {
    return (
      <div className={styles.ligneBoutons}>
        <Bouton variante="secondaire" onClick={() => setOuvert(true)}>
          Changer le code local
        </Bouton>
        {message ? (
          <span role="status" className={styles.aide}>
            {message}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <Formulaire
      libelle="Changer le code local"
      onSoumettre={() => void changer()}
      erreurs={erreurs}
      actions={
        <>
          <Bouton type="submit">Enregistrer le nouveau code</Bouton>
          <Bouton variante="discret" onClick={() => setOuvert(false)}>
            Annuler
          </Bouton>
        </>
      }
    >
      <Champ id="nouveau-code" libelle="Nouveau code à six chiffres" inputMode="numeric" maxLength={6} autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} erreur={erreurs.find((f) => f.champ === 'nouveau-code')?.message} required className={styles.codeChamp} />
      <Champ id="nouveau-code-confirmation" libelle="Confirmer le code" inputMode="numeric" maxLength={6} autoComplete="off" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} erreur={erreurs.find((f) => f.champ === 'nouveau-code-confirmation')?.message} required className={styles.codeChamp} />
    </Formulaire>
  );
}

function SectionSimulateur() {
  const sync = useSynchronisation();
  const travail = useTravailLocal();
  const [actif, setActif] = useState(simulateurActif());
  const [message, setMessage] = useState<string | null>(null);
  if (!basculeAutomatiquePermise()) return null;
  const basculer = () => {
    activerSimulateur(!actif);
    setActif(!actif);
    void synchroniseurPartage().rafraichirEtat();
  };
  const reaffecter = (id: string, libelle: string) => {
    const m = simulateurPartage().reaffecter(id);
    setMessage(m ? `Côté serveur simulé : ${libelle} réaffectée à ${m.equipe?.nom ?? 'une autre équipe'} (version ${m.version}). La prochaine synchronisation d'un rapport de cette mission produira un conflit.` : 'Mission inconnue du simulateur.');
  };
  return (
    <Section titre="Simulateur de l'API terrain (hors production)">
      <p className={styles.aide}>Tant que l&apos;API terrain réelle n&apos;est pas raccordée, un simulateur local tient lieu de serveur : données fictives, règles d&apos;idempotence et de conflit du contrat.</p>
      <Definitions elements={[{ terme: 'Source des données', valeur: sync.source === 'simulateur' ? 'Simulateur local' : 'Serveur' }]} />
      <div className={styles.ligneBoutons}>
        <Bouton variante="secondaire" onClick={basculer}>
          {actif ? 'Revenir au serveur réel' : 'Utiliser le simulateur'}
        </Bouton>
        {actif ? (
          <Bouton
            variante="discret"
            onClick={() => {
              simulateurPartage().reinitialiser();
              setMessage('Jeu de démonstration réinitialisé côté serveur simulé ; synchronisez pour le recharger.');
            }}
          >
            Réinitialiser le jeu de démonstration
          </Bouton>
        ) : null}
      </div>
      {actif && travail.donnees ? (
        <div className={styles.ligneBoutons}>
          {travail.donnees.missions.map((m) => (
            <Bouton key={m.id} petit variante="discret" onClick={() => reaffecter(m.id, m.intervention.numero)}>
              Simuler une réaffectation de {m.intervention.numero}
            </Bouton>
          ))}
        </div>
      ) : null}
      {message ? (
        <p role="status" className={styles.aide}>
          {message}
        </p>
      ) : null}
    </Section>
  );
}

export function PageReglages() {
  const { session, modeLocal, pwa } = useZoneTerrain();
  const { deconnecter } = useSession();
  const sync = useSynchronisation();
  const navigate = useNavigate();
  const [taille, setTaille] = useState<{ photos: number; operations: number; octets: number } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [installation, setInstallation] = useState<string | null>(null);
  useEffect(() => {
    let actif = true;
    void tailleDonnees().then((t) => actif && setTaille(t));
    return () => {
      actif = false;
    };
  }, [sync.revision]);

  const attente = sync.attente.operations + sync.attente.photos;
  const vider = async () => {
    await viderDonneesTravail();
    await synchroniseurPartage().rafraichirEtat();
    setMessage('Données de travail effacées de cet appareil ; synchronisez pour les retélécharger.');
  };
  const quitter = async () => {
    await fermerSessionLocale();
    await supprimerBase();
    await deconnecter();
    navigate('/connexion', { replace: true });
  };
  const installer = async () => {
    const r = await proposerInstallation();
    setInstallation(r === 'acceptee' ? "Installation acceptée : l'application s'ouvre désormais depuis l'écran d'accueil." : r === 'refusee' ? 'Installation refusée pour cette fois.' : 'Le navigateur ne propose pas l\'installation ici : utilisez son menu « Ajouter à l\'écran d\'accueil ».');
  };

  return (
    <>
      <EnTeteTerrain titre="Réglages" sousTitre={`${session.nom} · ${session.identifiant}`} />
      <Section titre="Compte et session locale">
        <Definitions
          elements={[
            { terme: 'Collectivité', valeur: session.collectivite ?? 'Non renseignée' },
            { terme: 'Session serveur', valeur: modeLocal ? <TexteEtat libelle="À rouvrir (hors connexion ou expirée)" ton="attention" /> : <TexteEtat libelle="Ouverte" ton="ok" /> },
            { terme: 'Session locale valable jusqu\'au', valeur: dateHeure(session.expire_le) },
            { terme: 'Renouvelée le', valeur: dateHeure(session.renouvelee_le) },
          ]}
        />
        <SectionCode />
        {modeLocal ? (
          <p className={styles.aide}>
            <Link to="/connexion" state={{ depuis: '/terrain' }}>
              Se reconnecter au serveur
            </Link>{' '}
            pour synchroniser et renouveler la session locale.
          </p>
        ) : null}
      </Section>
      <Section titre="Synchronisation">
        <BlocSynchronisation />
      </Section>
      <SectionCarteHorsConnexion />
      <Section titre="Application">
        <Definitions
          elements={[
            { terme: 'Service worker', valeur: !pwa.supporte ? 'Non pris en charge par ce navigateur' : pwa.erreur ? `Erreur : ${pwa.erreur}` : pwa.pretHorsConnexion ? <TexteEtat libelle="Prêt : l'application s'ouvre sans réseau" ton="ok" /> : pwa.enregistre ? 'Enregistré, en cours de préparation' : import.meta.env.DEV ? 'Absent en développement (version construite seulement)' : 'Non enregistré' },
            { terme: 'Installation', valeur: pwa.installee ? 'Installée sur cet appareil' : pwa.installable ? 'Proposée par le navigateur' : 'Par le menu du navigateur (« Ajouter à l\'écran d\'accueil »)' },
          ]}
        />
        <div className={styles.ligneBoutons}>
          {pwa.installable && !pwa.installee ? (
            <Bouton icone={Download} onClick={() => void installer()}>
              Installer l&apos;application
            </Bouton>
          ) : null}
          {pwa.miseAJourDisponible ? (
            <Bouton variante="secondaire" icone={RefreshCw} onClick={() => window.location.reload()}>
              Recharger la nouvelle version
            </Bouton>
          ) : null}
        </div>
        {installation ? (
          <p role="status" className={styles.aide}>
            {installation}
          </p>
        ) : null}
      </Section>
      <Section titre="Données sur cet appareil">
        <Definitions elements={[{ terme: 'Opérations enregistrées', valeur: taille ? String(taille.operations) : '…' }, { terme: 'Photos', valeur: taille ? `${taille.photos} (${tailleLisible(taille.octets)})` : '…' }, { terme: 'En attente de synchronisation', valeur: String(attente) }]} />
        {attente > 0 ? <p className={styles.aide}>{attente} élément(s) attendent la synchronisation : videz le cache seulement après les avoir synchronisés, sinon ils seront perdus.</p> : null}
        <ConfirmationIntegree libelle="Vider le cache (travail, opérations, photos)" question={attente > 0 ? `${attente} élément(s) non synchronisés seront perdus. Effacer quand même ?` : 'Les missions, tournées, ordres et photos enregistrés seront effacés de cet appareil. Continuer ?'} libelleConfirmer="Effacer" onConfirmer={() => void vider()} />
        {message ? (
          <p role="status" className={styles.aide}>
            {message}
          </p>
        ) : null}
      </Section>
      <SectionSimulateur />
      <Section titre="Déconnexion">
        <p className={styles.aide}>La déconnexion efface la session locale, son code et toutes les données de terrain de cet appareil.</p>
        <ConfirmationIntegree libelle="Se déconnecter et effacer cet appareil" question={attente > 0 ? `${attente} élément(s) non synchronisés seront perdus. Se déconnecter quand même ?` : 'Toutes les données de terrain seront effacées de cet appareil. Se déconnecter ?'} libelleConfirmer="Se déconnecter" onConfirmer={() => void quitter()} />
      </Section>
    </>
  );
}
